import React, { Component, Fragment } from 'react';
import { any, arrayOf, bool, func, number, oneOf, string } from 'prop-types';
import { get } from 'lodash';
import * as filePickerModule from '../../state/modules/filePicker';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Button } from '@material-ui/core/index';
import { Delete } from '@material-ui/icons';
import CheckCircle from '@material-ui/icons/CheckCircle';
import RemoveCircle from '@material-ui/icons/RemoveCircle';
import green from '@material-ui/core/colors/green';
import red from '@material-ui/core/colors/red';
import Warning from '@material-ui/icons/Warning';
import yellow from '@material-ui/core/colors/yellow';
import { guid } from '../../helpers/filePickerUtil';

import images from '../../../resources/images';
import styles from './styles.scss';
import classnames from 'classnames';

export class FilePicker extends Component {
  static propTypes = {
    id: string,
    sourceImage: any,
    defaultSourceImage: any,
    acceptedFileTypes: arrayOf(string),
    onPick: func,
    uploadRequest: func,
    replaceButtonLabel: string,
    className: string,
    progressPercent: number,
    success: bool,
    error: bool,
    warning: bool,
    deletable: bool,
    pickerState: oneOf(['selecting', 'uploading', 'complete'])
  };

  static defaultProps = {
    multiple: false,
    deletable: true,
    acceptedFileTypes: ['image/*'],
    replaceButtonLabel: 'REPLACE',
    defaultSourceImage: images.browseUpload,
    id: guid()
  };

  state = {
    displayLogoOptions: false,
    isFileDeleted: false
  };

  //---------------------------------------------------------------------
  //------------------------Interaction Functions------------------------
  //---------------------------------------------------------------------
  handleMouseEnter = () => {
    this.setState({ displayLogoOptions: true });
  };

  handleMouseLeave = () => {
    this.setState({ displayLogoOptions: false });
  };

  handleFileSelection = (event) => {
    const { onPick, id, uploadRequest } = this.props;
    const selectedFile = get(event, 'target.files[0]');

    if (!!selectedFile) {
      this.resetLogo();
      this.setState({ isFileDeleted: false });
      uploadRequest && uploadRequest(id, selectedFile, onPick);
    }
  };

  handleButtonClick = () => {
    const { sourceImage } = this.props;

    if (!sourceImage) {
      this.hiddenFileInput.current.click();
    }
  };

  handleFileDelete = () => {
    this.resetLogo();
    this.setState({ isFileDeleted: true });
  };

  handleFileReplace = () => {
    this.setState({ isFileDeleted: true });
    this.hiddenFileInput.current.click();
  };

  resetLogo() {
    const { onPick } = this.props;

    onPick && onPick([{ getUrl: null, unsignedUrl: null, url: null }]);
  }

  //---------------------------------------------------------------------
  //---------------------------Draw Functions----------------------------
  //---------------------------------------------------------------------
  drawLogoOptions = () => {
    const { replaceButtonLabel, deletable } = this.props;

    return (
      <div className={classnames(styles.options, { [styles.nonDeletable]: !deletable })}>
        <Button
          data-test="filePickerReplaceBtn"
          variant="contained"
          color="primary"
          onClick={this.handleFileReplace}
        >
          {replaceButtonLabel}
        </Button>
        {
          deletable &&
          <Button
            data-test="filePickerRemoveBtn"
            className={styles.deleteButton}
            variant="contained"
            color="primary"
            onClick={this.handleFileDelete}
          >
            <Delete />
          </Button>
        }
      </div>
    );
  };

  drawCompleteStatus() {
    const { success, error, warning } = this.props;

    let completeStatus = {
      [success]: 'success',
      [error]: 'failure',
      [warning]: 'warning'
    }[true];

    const icon = {
      success: (
        <CheckCircle
          classes={{ root: styles.successIcon }}
          style={{ fill: green[500] }}
          data-testtarget="successIcon"
        />
      ),
      failure: (
        <RemoveCircle
          classes={{ root: styles.errorIcon }}
          style={{ fill: red[500] }}
          data-testtarget="failureIcon"
        />
      ),
      warning: (
        <Warning
          classes={{ root: styles.errorIcon }}
          style={{ fill: yellow[500] }}
          data-testtarget="warnIcon"
        />
      )
    }[completeStatus];

    return (
      <div className={styles.resolutionIconContainer}>
        {icon}
        {completeStatus !== 'success' && this.drawProgressMessage()}
      </div>
    );
  }

  drawProgressMessage = () => {
    const { progressMessage } = this.props;

    return (<div className={styles.progressMessage}>{progressMessage}</div>);
  };

  drawProgressDialog = () => {
    const { progressPercent } = this.props;

    return (
      <div className={styles.circularProgressContainer}>
        <CircularProgress
          className={styles.circularProgress}
          size={90}
          thickness={1}
        />
        <div
          className={
            progressPercent < 10
              ? styles.initialPercent
              : progressPercent <= 99
                ? styles.midPercent
                : styles.totalPercent
          }
        >
          {`${progressPercent}%`}
        </div>
      </div>
    );
  };

  hiddenFileInput = React.createRef();
  render() {
    const { sourceImage, defaultSourceImage, acceptedFileTypes, multiple, pickerState, className } = this.props;
    const { displayLogoOptions, isFileDeleted } = this.state;

    return (
      <Fragment>
        <div className={classnames(styles.deletableFilePicker, className)}
          onMouseEnter={this.handleMouseEnter}
          onMouseLeave={this.handleMouseLeave}
        >
          {
            pickerState === 'uploading' ? this.drawProgressDialog() :
              pickerState === 'complete' && !sourceImage && !isFileDeleted ? this.drawCompleteStatus() :
                <Fragment>
                  <Button
                    disableFocusRipple
                    disableRipple
                    className={classnames(styles.imageButton, { [styles.imageLoaded]: !!sourceImage })}
                    onClick={this.handleButtonClick}
                  >
                    <input
                      data-test="filePickerInput"
                      className={classnames(styles.image, { [styles.imageLoaded]: !!sourceImage })}
                      type="image"
                      src={sourceImage ? sourceImage : defaultSourceImage}
                    />
                  </Button>
                  <input
                    id="file"
                    type="file"
                    multiple={multiple}
                    style={{ display: 'none' }}
                    onChange={this.handleFileSelection}
                    accept={acceptedFileTypes.join(',')}
                    ref={this.hiddenFileInput}
                  />
                  {sourceImage && displayLogoOptions && this.drawLogoOptions()}
                </Fragment>
          }
        </div>
      </Fragment>
    );
  }
}

import { connect } from 'react-redux';
import { namespace } from '../../state/modules/filePicker';

export default connect(
  (state) => {
    const id = get(state, [namespace, 'id']);
    return {
      pickerState: filePickerModule.state(state, id),
      progressPercent: filePickerModule.progressPercent(state, id),
      success: filePickerModule.didSucceed(state, id),
      error: filePickerModule.didError(state, id),
      warning: filePickerModule.didWarn(state, id)
    };
  },
  {
    uploadRequest: filePickerModule.uploadRequest
  }
)(FilePicker);
